import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, Redirect, useLocation, useParams } from 'react-router-dom';
import {
  PiArrowLeftLight,
  PiCaretLineLeftLight,
  PiCaretLineRightLight,
  PiCaretRightLight,
} from 'react-icons/pi';
import { RootState } from '../../../store';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { LayoutContext } from '../../../contexts/LayoutContext';
import Header from '../../shared/TailwindComponents/Header';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import {
  readScoped,
  scopeKey,
  writeScoped,
  LayoutStorage,
} from '../../../services/layout/layoutStore';
import useLayoutUserId from '../../../services/layout/useLayoutUserId';
import { SettingsNotAuthorized } from './SettingsSection';
import { LIST_SECTION_MIN_HEIGHT } from './listSectionLayout';
import {
  SETTINGS_SECTIONS,
  getSettingsSection,
  isSettingsSectionAvailable,
  isSettingsSectionAuthorized,
  resolveLegacySettingsTarget,
  settingsSectionPath,
  visibleSettingsGroups,
  SettingsSectionDef,
  SettingsViewer,
} from './settingsRegistry';

/**
 * The settings shell.
 *
 * `/settings` is the index (grouped cards); `/settings/:sectionId` is one
 * section beside a grouped rail. Both are driven entirely by the registry, so
 * a new section needs no change here.
 *
 * THE SHAPE: rail and content are two different surfaces, not one flat field.
 * The rail is a glass panel that floats at the left edge - sticky, self-
 * scrolling, hairline-separated from the canvas; the content sits on the
 * canvas in a fixed-width reading column so a settings body never stretches
 * to a 2000px monitor. Where you are is stated twice on the active item - the
 * `nav-pill-active` liquid pill it shares with every other tab bar in the
 * app, plus a champagne marker - because a pill alone reads as hover.
 *
 * The rail COLLAPSES to icons, remembered per user (see `useSettingsRail`).
 *
 * Narrow viewports drop the rail rather than squeezing it: the index IS the
 * navigation there, and a section gets a back link to it.
 *
 * THE SCROLL CONTRACT - the thing that was wrong, and what replaces it.
 *
 * Settings used to ride the page scroller in `PrivateLayout`'s `<main>`: on a
 * long section (Recycle bin, Users) the rail and the section header scrolled
 * away with the list, rows slid up behind the header, and a stray horizontal
 * bar appeared at the bottom of the window. So:
 *
 *   The page does not scroll. `setIsContentScrollable(false)` puts `<main>`
 *   into `overflow-hidden` - the same switch every other viewport-locked page
 *   here uses - and this shell is a full-height flex column beneath it. It is
 *   re-asserted on every section change, because a section body may flip the
 *   flag itself and restore it on unmount.
 *
 *   THREE scrollers, never the window: the rail's list, the content pane, and
 *   whatever a section nests inside the pane. Each is `flex-1 min-h-0
 *   overflow-y-auto` under a `min-h-0` parent - without `min-h-0` a flex child
 *   refuses to shrink below its content and the overflow escapes upward, which
 *   is how the page scroll came back every previous time.
 *
 *   The header - breadcrumb row, glyph/title/description - is a SIBLING above
 *   the content scroller, not the first thing inside it. That is what makes it
 *   stay put with nothing bleeding under it.
 *
 *   Nothing scrolls sideways at this level: every column is `min-w-0` and the
 *   content pane is `overflow-x-hidden`, so a wide table has to carry its own
 *   `overflow-x-auto` instead of pushing the whole layout out.
 *
 * A section body that wants its own inner scroll (a long list with a fixed
 * toolbar) declares `layout: 'list'` in the registry. That is the ONLY way to
 * get a resolvable height in here: the pane's reading column is a content-sized
 * block for a form section, and a `h-full` against one of those silently
 * becomes `auto`. See `SETTINGS_PANE_FILL_COLUMN` and `listSectionLayout`.
 */

/** Reads the viewer once, from the two sources that decide what settings exist. */
export const useSettingsViewer = (): SettingsViewer => {
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { accessRights } = usePermissions();
  return useMemo(
    () => ({
      isOrgAdmin: !!(userInfo as any)?.is_org_admin,
      orgType: (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || null,
      accessRights: accessRights as unknown as Record<string, boolean | undefined>,
    }),
    [userInfo, accessRights]
  );
};

/* ------------------------------------------------------------------ */
/*  Rail collapse - persisted per user                                 */
/* ------------------------------------------------------------------ */

/**
 * The rail's collapsed state is a layout, so it lives where layouts live:
 * `pons:layout:v1:<userId>:shell:settings-rail`. Same reason as every other
 * surface - a shared browser must not hand one person's arrangement to the
 * next, and a bare `localStorage` key cannot say whose it is.
 */
const RAIL_SCOPE = { kind: 'shell', name: 'settings-rail' } as const;

export const settingsRailKey = (userId: string): string => scopeKey({ ...RAIL_SCOPE, userId });

/** Stored state is untrusted: anything that is not an explicit `true` is expanded. */
export const readSettingsRailCollapsed = (
  userId: string,
  storage?: LayoutStorage | null
): boolean => {
  const raw = readScoped(settingsRailKey(userId), storage);
  return !!raw && typeof raw === 'object' && (raw as any).collapsed === true;
};

export const writeSettingsRailCollapsed = (
  userId: string,
  collapsed: boolean,
  storage?: LayoutStorage | null
): void => {
  writeScoped(settingsRailKey(userId), { collapsed }, storage);
};

/**
 * Collapsed state + a toggle, read before the first paint so the rail never
 * renders expanded and then snaps shut. The id can arrive late on a first-ever
 * login (`useLayoutUserId` starts at `anon`), so the state is re-read when it
 * changes rather than being read once and kept.
 */
export const useSettingsRail = (): { collapsed: boolean; toggle: () => void } => {
  const userId = useLayoutUserId();
  const [collapsed, setCollapsed] = useState<boolean>(() => readSettingsRailCollapsed(userId));

  useEffect(() => {
    setCollapsed(readSettingsRailCollapsed(userId));
  }, [userId]);

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      writeSettingsRailCollapsed(userId, next);
      return next;
    });
  }, [userId]);

  return { collapsed, toggle };
};

/* ------------------------------------------------------------------ */
/*  Pieces                                                             */
/* ------------------------------------------------------------------ */

const SectionIcon: React.FC<{ section: SettingsSectionDef; className?: string }> = ({
  section,
  className,
}) => {
  const Icon = section.icon;
  return <Icon className={className || 'w-5 h-5'} />;
};

/** The icon tile that opens every section header and index card. */
const SectionGlyph: React.FC<{ section: SettingsSectionDef; className?: string }> = ({
  section,
  className,
}) => (
  <span
    className={clsx(
      'flex shrink-0 items-center justify-center rounded-lg bg-surface-sunken/70 border border-hairline-soft text-champagne',
      className || 'h-10 w-10'
    )}
  >
    <SectionIcon section={section} className="w-5 h-5" />
  </span>
);

/**
 * ONE recipe for a rail item, both states.
 *
 * Collapsing must not change what an item IS - same pill container, same
 * radius, same active treatment, same height. Only the label and the
 * horizontal padding differ, so the rail does not appear to become a
 * different component when it narrows.
 */
const RAIL_ITEM_BASE =
  'nav-pill group relative flex min-h-[2.25rem] items-center rounded-md text-[13px] transition-colors duration-200 ease-slide motion-reduce:transition-none';

/** Muted, well-tracked, and the same in the rail and on the index. */
const RAIL_GROUP_LABEL =
  'text-[10px] font-medium uppercase tracking-[0.14em] text-fg-muted';

/** Every icon in the rail is this size and weight - none of them optical one-offs. */
const RAIL_ICON = 'h-[18px] w-[18px] shrink-0 transition-colors duration-200 ease-slide';

export const SettingsRail: React.FC<{
  viewer: SettingsViewer;
  activeId?: string;
  collapsed?: boolean;
  onToggle?: () => void;
}> = ({ viewer, activeId, collapsed = false, onToggle }) => {
  const { t } = useTranslation();
  const groups = visibleSettingsGroups(viewer);
  const toggleLabel = collapsed ? t('Expand settings menu') : t('Collapse settings menu');

  return (
    <nav
      aria-label={t('Settings')}
      data-collapsed={collapsed ? 'true' : 'false'}
      className={clsx(
        // h-full + min-h-0: the rail is a full-height column that scrolls
        // itself, not a block that grows the page until the content moves.
        'hidden lg:flex h-full min-h-0 shrink-0 flex-col transition-[width] duration-300 ease-slide motion-reduce:transition-none',
        collapsed ? 'w-[4.5rem]' : 'w-64'
      )}
    >
      <div className="glass flex h-full min-h-0 flex-col overflow-hidden rounded-lg shadow-sm">
        {/* The panel's own header, hairline-closed - the toggle lives here
            rather than floating over the list, so it never covers an item. */}
        <div
          className={clsx(
            'flex shrink-0 items-center gap-2 border-b border-hairline-soft px-3 py-2.5',
            collapsed && 'justify-center'
          )}
        >
          {!collapsed && (
            <span className={clsx('min-w-0 flex-1 truncate', RAIL_GROUP_LABEL)}>
              {t('Settings')}
            </span>
          )}
          <Tooltip title={toggleLabel} placement="right" arrow>
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!collapsed}
              aria-label={toggleLabel}
              className="nav-pill flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors duration-200 ease-slide hover:text-fg-strong motion-reduce:transition-none"
            >
              {collapsed ? (
                <PiCaretLineRightLight className="h-4 w-4 rtl:-scale-x-100" />
              ) : (
                <PiCaretLineLeftLight className="h-4 w-4 rtl:-scale-x-100" />
              )}
            </button>
          </Tooltip>
        </div>

        <div
          data-testid="settings-rail-scroll"
          className="flex-1 min-h-0 space-y-6 overflow-y-auto overflow-x-hidden custom-scrollbar-main px-2 py-3"
        >
          {groups.map(({ group, sections }) => (
            <div key={group.id} className="space-y-0.5">
              {collapsed ? (
                // Collapsed there is no room for the word. A short inset rule
                // keeps the grouping readable without becoming a divider that
                // competes with the pills; the name stays for screen readers.
                <div className="px-3 pb-1.5 pt-0.5">
                  <span className="sr-only">{t(group.label)}</span>
                  <span aria-hidden className="block h-px rounded-full bg-hairline-soft" />
                </div>
              ) : (
                <p className={clsx('px-2.5 pb-1.5 pt-0.5', RAIL_GROUP_LABEL)}>{t(group.label)}</p>
              )}
              {sections.map((section) => {
                const active = section.id === activeId;
                const item = (
                  <Link
                    key={section.id}
                    to={settingsSectionPath(section.id)}
                    aria-current={active ? 'page' : undefined}
                    title={collapsed ? undefined : t(section.description)}
                    className={clsx(
                      RAIL_ITEM_BASE,
                      collapsed ? 'justify-center px-0' : 'gap-3 px-2.5 py-1.5',
                      active
                        ? 'nav-pill-active font-medium text-fg-strong'
                        : 'text-fg-body hover:text-fg-strong'
                    )}
                  >
                    {/* The champagne marker is the same object in both states —
                        an edge tick on the pill, never a second visual idea. */}
                    {active && (
                      <span
                        aria-hidden
                        className="absolute start-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-e-full bg-champagne"
                      />
                    )}
                    <SectionIcon
                      section={section}
                      className={clsx(
                        RAIL_ICON,
                        active ? 'text-champagne' : 'text-fg-muted group-hover:text-fg-body'
                      )}
                    />
                    {collapsed ? (
                      <span className="sr-only">{t(section.label)}</span>
                    ) : (
                      <span className="min-w-0 flex-1 truncate">{t(section.label)}</span>
                    )}
                  </Link>
                );

                // Collapsed, the label is the tooltip - the platform's MUI one,
                // not a `title` attribute that appears a second later.
                return collapsed ? (
                  <Tooltip key={section.id} title={t(section.label)} placement="right" arrow>
                    {item}
                  </Tooltip>
                ) : (
                  item
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

/**
 * The index's header. A SIBLING of the scroller, exactly like a section's —
 * the index is a long grid on a small org, and its title should no more scroll
 * away than a section's does.
 */
export const SettingsIndexHeader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <header className="border-b border-hairline-soft pb-4">
      <h1 className="text-lg font-medium text-fg-strong">{t('Settings')}</h1>
      <p className="mt-0.5 text-sm text-fg-muted">
        {t('Your account, your organization, and how the platform behaves.')}
      </p>
    </header>
  );
};

export const SettingsIndex: React.FC<{ viewer: SettingsViewer }> = ({ viewer }) => {
  const { t } = useTranslation();
  const groups = visibleSettingsGroups(viewer);

  return (
    <div className="space-y-8">
      {groups.map(({ group, sections }) => (
        <section key={group.id} className="space-y-3">
          <div className="flex items-center gap-3">
            <h2 className={RAIL_GROUP_LABEL}>{t(group.label)}</h2>
            <span aria-hidden className="h-px flex-1 bg-hairline-soft" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {sections.map((section) => (
              <Link
                key={section.id}
                to={settingsSectionPath(section.id)}
                className="group block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
              >
                <GlassCard className="h-full transition-shadow duration-200 ease-slide hover:shadow-md motion-reduce:transition-none">
                  <div className="flex items-start gap-3">
                    <SectionGlyph section={section} className="h-9 w-9" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-fg-strong">{t(section.label)}</p>
                      <p className="mt-1 text-xs text-fg-muted">{t(section.description)}</p>
                    </div>
                    <PiCaretRightLight className="mt-0.5 w-4 h-4 shrink-0 text-fg-muted transition-transform duration-200 ease-slide group-hover:translate-x-0.5 motion-reduce:transition-none rtl:-scale-x-100" />
                  </div>
                </GlassCard>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

/** The one header rhythm every section wears: glyph + title + description. */
export const SettingsSectionHeader: React.FC<{ section: SettingsSectionDef }> = ({ section }) => {
  const { t } = useTranslation();
  return (
    <header className="flex items-start gap-3 border-b border-hairline-soft pb-4">
      <SectionGlyph section={section} />
      <div className="min-w-0">
        <h1 className="text-lg font-medium text-fg-strong">{t(section.label)}</h1>
        <p className="mt-0.5 text-sm text-fg-muted">{t(section.description)}</p>
      </div>
    </header>
  );
};

/**
 * The class the content scroller wears, and the contract a section body plugs
 * into: this element is the ONLY vertical scroller in the pane, it is a
 * `min-h-0` flex column, and it does not scroll sideways.
 *
 * A section that just lays out fields needs to know none of this - it flows
 * and the pane scrolls it. A section with its own inner scroll (a list under a
 * fixed toolbar) makes its root `h-full min-h-0 flex flex-col` and puts
 * `flex-1 min-h-0 overflow-y-auto` on the list.
 */
export const SETTINGS_PANE_SCROLL =
  'flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden custom-scrollbar-main [scrollbar-gutter:stable]';

/**
 * The content pane's own breathing room - INSIDE the scroller, never outside.
 *
 * Outside it becomes an inset that pushes the scrollbar off the screen edge;
 * inside it is padding the content sits within while the scroller still runs
 * full-bleed to the window.
 */
export const SETTINGS_PANE_X = 'px-6 lg:px-8';

/**
 * The reading column a `layout: 'list'` section is mounted in - the link that
 * was missing from the height chain.
 *
 * A form section's column is a plain block sized by its fields, and that is
 * right for it. A list section's is not: a table that owns its own scroller has
 * to be told how tall it may be, and `h-full` against a content-sized block does
 * not resolve - CSS falls back to `auto`, the table grows to all its rows, and
 * nothing scrolls. That is why the list shell used to state `h-[60vh]`, which
 * fixed the scrolling and introduced the real complaint: a fraction of the
 * VIEWPORT ignores the pane, so on a tall screen the pager sat mid-canvas with
 * the rest of the pane empty under it. Making this column a real flex column is
 * what lets `LIST_SECTION_SHELL` be `h-full` and simply fill.
 *
 * `min-h` and not `min-h-0`: under ~20rem there is no room for a row between
 * the toolbar and the pager, so the column stops shrinking and the pane - which
 * is the scroller - takes over. Deliberately NOT `min-h-0`, which would let it
 * shrink to nothing and hand a short viewport an unusable sliver of table.
 */
export const SETTINGS_PANE_FILL_COLUMN = `flex flex-1 flex-col ${LIST_SECTION_MIN_HEIGHT}`;

/**
 * The header's matching padding: the same, plus the 8px the scrollbar takes out
 * of the pane (`custom-scrollbar-main` is a fixed 8px, and
 * `scrollbar-gutter: stable` reserves it whether or not it is in use). 24+8=32
 * (`pe-8`), 32+8=40 (`pe-10`), so the two centred columns line up exactly.
 */
export const SETTINGS_HEADER_X = 'ps-6 lg:ps-8 pe-8 lg:pe-10';

const SectionBody: React.FC<{ section: SettingsSectionDef }> = ({ section }) => {
  const Component = section.component;

  // Phase 1 renders the existing section components unchanged.
  return (
    <Suspense fallback={<Skeleton variant="form" />}>
      <Component />
    </Suspense>
  );
};

const SettingsLayout: React.FC = () => {
  const { t } = useTranslation();
  const { sectionId } = useParams<{ sectionId?: string }>();
  const location = useLocation();
  const viewer = useSettingsViewer();
  const { collapsed, toggle } = useSettingsRail();
  const { setIsContentScrollable } = useContext(LayoutContext);

  // The page scroller is off for the whole time settings is mounted. Keyed on
  // the path as well: a section body may switch it off and restore it on its
  // own unmount, and that restore lands while this shell is still mounted.
  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable, location.pathname]);

  // `/settings?tab=notifications` - the shape the old tabbed page used.
  const legacyTarget = !sectionId ? resolveLegacySettingsTarget('/settings', location.search) : null;
  if (legacyTarget) return <Redirect to={legacyTarget} />;

  const section = getSettingsSection(sectionId);

  // An id that is not in the registry, or is switched off for this org, is not
  // a page - send it back to the index rather than showing an empty shell.
  if (sectionId && (!section || !isSettingsSectionAvailable(section, viewer))) {
    return <Redirect to="/settings" />;
  }

  const authorized = section ? isSettingsSectionAuthorized(section, viewer) : true;

  // Only a list section that is actually being drawn fills - the index and the
  // not-authorized card are ordinary content-sized blocks.
  const fillsPane = !!section && authorized && section.layout === 'list';

  return (
    /*
      `h-full` here is only worth anything because `PrivateLayout` hands down a
      DEFINITE height (`h-screen`, with `<main>` a `min-h-0` flex column) - see
      the note there. Percentages resolve against a definite parent or not at
      all, and "not at all" is silently `auto`: this box grows to its content,
      both columns grow with it, and the window scrolls instead. If settings
      ever starts scrolling as one page again, that chain is the first thing to
      check, not the classes below.

      flex-shrink is deliberate: 100% of main plus a license banner overflows
      main, and `min-h-0` lets this box give the banner its band back.
    */
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="shrink-0">
        <Header />
      </div>
      {/*
        THE ROW HAS NO HORIZONTAL PADDING, and that is the point.

        Its own `px-4 lg:px-6` used to inset the content column, so the content
        scroller ended a screw's width short of the window and its scrollbar
        floated in mid-canvas. `<main>` already reaches the viewport's right
        edge - `PrivateLayout` adds no padding and the notifications panel is
        `fixed` - so the inset was ours alone. The column now runs full-bleed to
        that edge and every bit of the spacing lives INSIDE the scroller as
        padding. Same look, scrollbar where the screen ends.

        The rail carries its own margins instead, because it is a floating
        panel rather than a bleed surface.
      */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex h-full min-h-0 shrink-0 py-6 ps-4 pe-5 lg:ps-6">
          <SettingsRail
            viewer={viewer}
            activeId={section?.id}
            collapsed={collapsed}
            onToggle={toggle}
          />
        </div>
        {/* The edge between navigation and content, stated once. The rail is a
            floating panel and the content is on the canvas, so without a rule
            the boundary is only implied by the gap. */}
        <div
          data-testid="settings-rail-divider"
          aria-hidden
          className="my-6 hidden w-px shrink-0 bg-hairline-soft lg:block"
        />
        {/* min-w-0 is what stops a wide section from widening the row and
            handing the window a horizontal scrollbar. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/*
            The header is outside the scroller so it cannot scroll away, which
            means it does not lose the 8px the scrollbar takes - hence the wider
            right padding here and `scrollbar-gutter: stable` there. Without the
            pair, the centred reading column steps 4px sideways between the
            title and the content under it.
          */}
          <div className={clsx('shrink-0 pt-6', SETTINGS_HEADER_X)}>
            <div className="mx-auto w-full max-w-4xl">
              {section && (
                <Link
                  to="/settings"
                  className="lg:hidden mb-4 inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-body"
                >
                  <PiArrowLeftLight className="w-4 h-4 rtl:-scale-x-100" />
                  {t('All settings')}
                </Link>
              )}
              {section ? <SettingsSectionHeader section={section} /> : <SettingsIndexHeader />}
            </div>
          </div>
          <div
            data-testid="settings-content-scroll"
            className={clsx(SETTINGS_PANE_SCROLL, SETTINGS_PANE_X, 'pb-10 pt-6')}
          >
            {/*
              A list section's column FILLS the pane; a form's is sized by its
              fields. That is the whole difference between the two shapes, and
              it is stated once, here - see `SETTINGS_PANE_FILL_COLUMN`.
            */}
            <div className={clsx('mx-auto w-full max-w-4xl', fillsPane && SETTINGS_PANE_FILL_COLUMN)}>
              {!section && <SettingsIndex viewer={viewer} />}
              {section && !authorized && (
                <GlassCard>
                  <SettingsNotAuthorized />
                </GlassCard>
              )}
              {section && authorized && <SectionBody section={section} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Redirects the pre-framework settings URLs onto their registry sections. */
export const SettingsLegacyRedirect: React.FC = () => {
  const location = useLocation();
  const target = resolveLegacySettingsTarget(location.pathname, location.search);
  return <Redirect to={target || '/settings'} />;
};

export { SETTINGS_SECTIONS };
export default SettingsLayout;
